import { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { useSearchParams, Link } from "react-router-dom";
import { Check, Play, Shield, ChevronDown, ChevronUp, Target, FileText, Calendar, DollarSign, Palette, Mail, CheckCircle2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

function useReveal(threshold = 0.12) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.unobserve(e.target); } }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, visible };
}

function Reveal({ children, delay = 0, className = "" }: { children: React.ReactNode; delay?: number; className?: string }) {
  const { ref, visible } = useReveal(0.1);
  return (
    <div ref={ref} className={className} style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(20px)", transition: `all 0.6s cubic-bezier(.22,1,.36,1) ${delay}ms` }}>
      {children}
    </div>
  );
}

function FAQ({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b" style={{ borderColor: "#E5E7EB" }}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between py-4 text-left cursor-pointer bg-transparent border-none">
        <span className="font-semibold text-sm" style={{ color: "#1F2937" }}>{question}</span>
        {open ? <ChevronUp className="w-4 h-4 flex-shrink-0" style={{ color: "#9CA3AF" }} /> : <ChevronDown className="w-4 h-4 flex-shrink-0" style={{ color: "#9CA3AF" }} />}
      </button>
      {open && <p className="text-sm pb-4 leading-relaxed" style={{ color: "#6B7280" }}>{answer}</p>}
    </div>
  );
}

const valueItems = [
  { icon: <Target className="w-5 h-5" />, title: "AI Content Monetisation Playbook", desc: "Step-by-step system matched to your quiz result", value: "£97" },
  { icon: <FileText className="w-5 h-5" />, title: "27 Plug-and-Play AI Prompts", desc: "Copy, paste, customise for your niche", value: "£47" },
  { icon: <Calendar className="w-5 h-5" />, title: "90-Day Monetisation Content Calendar", desc: "Know exactly what to post and when", value: "£47" },
  { icon: <DollarSign className="w-5 h-5" />, title: "Digital Product Pricing Calculator", desc: "AI-powered spreadsheet to price your offers", value: "£27" },
  { icon: <Palette className="w-5 h-5" />, title: "Sales Page Copy Templates", desc: "Fill-in-the-blank templates for your first offer", value: "£47" },
  { icon: <Mail className="w-5 h-5" />, title: "Launch Sequence Email Swipe File", desc: "7 emails to launch your first product", value: "£47" },
];

const testimonials = [
  { name: "Sarah", role: "Instagram Creator, 3K followers", text: "I used the AI prompts to create my first digital product in a weekend. Made £340 in the first week." },
  { name: "Marcus", role: "YouTuber, 8K subscribers", text: "The content calendar alone saved me 10+ hours a month. Worth way more than £17." },
  { name: "Emma", role: "TikTok Creator, 5K followers", text: "The pricing calculator helped me realise I was massively undercharging. Doubled my revenue in a month." },
];

export default function SpecialOffer() {
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email") || "";
  const [orderBump, setOrderBump] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [countdown, setCountdown] = useState(15 * 60);

  useEffect(() => {
    const timer = setInterval(() => setCountdown(p => p > 0 ? p - 1 : 0), 1000);
    return () => clearInterval(timer);
  }, []);

  const minutes = Math.floor(countdown / 60);
  const seconds = countdown % 60;

  const handlePurchase = async () => {
    setPurchasing(true);
    try {
      const { data, error } = await supabase.functions.invoke("create-tripwire-checkout", {
        body: { email, orderBump },
      });
      if (error) throw error;
      if (data?.url) window.location.href = data.url;
    } catch (err: any) {
      toast.error("Something went wrong. Please try again.");
      setPurchasing(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "#FFFFFF" }}>
      <Helmet>
        <title>Special Offer — AI Creator Monetisation Toolkit | AI StoryCraft Studio</title>
        <meta name="description" content="Get the AI Creator Monetisation Toolkit: 27 done-for-you templates, AI prompts, and step-by-step playbooks for just £17." />
      </Helmet>

      {/* Confirmation Bar */}
      <div className="sticky top-0 z-50 w-full px-4 py-3" style={{ backgroundColor: "#ECFDF5", borderBottom: "1px solid #D1FAE5" }}>
        <div className="max-w-3xl mx-auto flex items-center gap-2">
          <CheckCircle2 className="w-5 h-5 flex-shrink-0" style={{ color: "#10B981" }} />
          <p className="text-sm" style={{ color: "#065F46" }}>
            Your full personalised report has been sent to <strong>{email || "your inbox"}</strong>. Check your inbox in 2 minutes.
          </p>
        </div>
      </div>

      <div className="mx-auto px-4 md:px-6" style={{ maxWidth: 820 }}>
        {/* Hero */}
        <Reveal>
          <div className="text-center pt-12 md:pt-16 pb-10">
            <p className="text-xs font-semibold uppercase tracking-widest mb-4" style={{ color: "#7C3AED", letterSpacing: "0.1em" }}>EXCLUSIVE OFFER FOR QUIZ COMPLETERS</p>
            <h1 className="text-2xl md:text-[2.5rem] font-extrabold leading-tight mb-4" style={{ color: "#1F2937" }}>
              Your Roadmap Showed You Where to Go — This Toolkit Shows You{" "}
              <span style={{ color: "#F59E0B" }}>Exactly How to Get There</span>
            </h1>
            <p className="text-base md:text-lg leading-relaxed max-w-2xl mx-auto" style={{ color: "#6B7280" }}>
              The AI Creator Monetisation Toolkit: 27 done-for-you templates, AI prompts, and step-by-step playbooks — built specifically for creators at your stage.
            </p>
          </div>
        </Reveal>

        {/* Video Placeholder */}
        <Reveal delay={100}>
          <div className="mb-10">
            <div className="w-full rounded-2xl overflow-hidden flex items-center justify-center cursor-pointer" style={{ aspectRatio: "16/9", background: "linear-gradient(135deg, #7C3AED, #6D28D9)" }}>
              <div className="text-center">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: "rgba(255,255,255,0.2)" }}>
                  <Play className="w-7 h-7 text-white ml-1" />
                </div>
                <p className="text-white font-semibold text-sm">Watch the 3-Minute Walkthrough</p>
              </div>
            </div>
            <p className="text-center text-sm mt-3" style={{ color: "#9CA3AF" }}>See exactly what's inside and how to use it today</p>
          </div>
        </Reveal>

        {/* Value Stack */}
        <Reveal delay={200}>
          <div className="mb-12">
            <h2 className="text-xl md:text-2xl font-bold text-center mb-8" style={{ color: "#1F2937" }}>Here's Everything Inside Your Toolkit:</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {valueItems.map((item, i) => (
                <div key={i} className="bg-white rounded-xl p-5 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md" style={{ border: "1px solid #E5E7EB" }}>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "#EDE9FE", color: "#7C3AED" }}>
                      {item.icon}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-sm mb-1" style={{ color: "#1F2937" }}>{item.title}</h3>
                      <p className="text-xs mb-1" style={{ color: "#6B7280" }}>{item.desc}</p>
                      <p className="text-xs line-through" style={{ color: "#9CA3AF" }}>Value: {item.value}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="text-center mt-6">
              <p className="text-lg font-bold" style={{ color: "#1F2937" }}>
                Total Value: <span className="line-through" style={{ color: "#9CA3AF" }}>£312</span>{" "}
                <span className="text-2xl" style={{ color: "#7C3AED" }}>Today Only: £17</span>
              </p>
            </div>
          </div>
        </Reveal>

        {/* Social Proof */}
        <Reveal delay={300}>
          <div className="mb-12">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {testimonials.map((t, i) => (
                <div key={i} className="bg-white rounded-xl p-5" style={{ border: "1px solid #E5E7EB" }}>
                  <p className="text-sm italic mb-3 leading-relaxed" style={{ color: "#374151" }}>"{t.text}"</p>
                  <div>
                    <p className="text-sm font-semibold" style={{ color: "#1F2937" }}>{t.name}</p>
                    <p className="text-xs" style={{ color: "#9CA3AF" }}>{t.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        {/* Pricing Section */}
        <Reveal delay={400}>
          <div className="rounded-2xl p-6 md:p-10 mb-12" style={{ backgroundColor: "#F8F7FF" }}>
            <h2 className="text-xl md:text-2xl font-bold text-center mb-2" style={{ color: "#1F2937" }}>Get Instant Access for Just £17</h2>
            <p className="text-sm text-center mb-6" style={{ color: "#6B7280" }}>
              That's less than the price of two coffees — for a system that can generate your first £500+ in revenue.
            </p>

            <div className="flex items-center justify-center gap-3 mb-8">
              <span className="text-2xl line-through" style={{ color: "#9CA3AF" }}>£312</span>
              <span className="text-5xl font-extrabold" style={{ color: "#7C3AED" }}>£17</span>
            </div>

            {/* Order Bump */}
            <div className="mb-6 rounded-xl p-4 cursor-pointer transition-all" onClick={() => setOrderBump(!orderBump)} style={{ border: `2px dashed ${orderBump ? "#7C3AED" : "#D1D5DB"}`, backgroundColor: orderBump ? "#F5F3FF" : "#FFFFFF" }}>
              <div className="flex items-start gap-3">
                <div className="w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5" style={{ borderColor: orderBump ? "#7C3AED" : "#D1D5DB", backgroundColor: orderBump ? "#7C3AED" : "transparent" }}>
                  {orderBump && <Check className="w-3 h-3 text-white" />}
                </div>
                <div>
                  <p className="font-semibold text-sm mb-1" style={{ color: "#1F2937" }}>✅ YES! Add the "AI Swipe File Vault" for just £12 more</p>
                  <p className="text-xs leading-relaxed" style={{ color: "#6B7280" }}>47 proven hooks, captions, and story frameworks used by creators earning £5K+/month from their content. Normally £47 — yours for £12 when you add it now.</p>
                </div>
              </div>
            </div>

            <button onClick={handlePurchase} disabled={purchasing} className="w-full font-bold text-lg py-4 rounded-full border-none text-white cursor-pointer transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] min-h-[56px]" style={{ backgroundColor: "#7C3AED", boxShadow: "0 4px 14px rgba(124,58,237,0.3)" }}>
              {purchasing ? "Processing..." : `Get Instant Access → £${orderBump ? "29" : "17"}`}
            </button>

            <div className="flex items-center justify-center gap-4 mt-4 flex-wrap">
              {["Instant digital delivery", "30-day money-back guarantee", "Lifetime access"].map((t, i) => (
                <span key={i} className="text-xs" style={{ color: "#9CA3AF" }}>{i > 0 ? "· " : ""}{t}</span>
              ))}
            </div>
          </div>
        </Reveal>

        {/* Guarantee */}
        <Reveal delay={500}>
          <div className="text-center mb-12">
            <Shield className="w-10 h-10 mx-auto mb-3" style={{ color: "#7C3AED" }} />
            <h3 className="font-bold text-lg mb-2" style={{ color: "#1F2937" }}>30-Day Money-Back Guarantee</h3>
            <p className="text-sm max-w-lg mx-auto leading-relaxed" style={{ color: "#6B7280" }}>
              If the toolkit doesn't help you take a meaningful step toward monetising your audience, email us for a full refund. No questions asked.
            </p>
          </div>
        </Reveal>

        {/* FAQ */}
        <Reveal delay={600}>
          <div className="mb-12">
            <h2 className="text-xl font-bold text-center mb-6" style={{ color: "#1F2937" }}>Frequently Asked Questions</h2>
            <div className="max-w-2xl mx-auto">
              <FAQ question="What exactly do I get?" answer="You get the full AI Creator Monetisation Toolkit including the Monetisation Playbook, 27 AI Prompts, 90-Day Content Calendar, Pricing Calculator, Sales Page Templates, and the Launch Email Swipe File. Everything is delivered digitally — instant access." />
              <FAQ question="Will this work for my niche?" answer="Yes — the templates are designed to be customised to any niche. Whether you're in fitness, finance, cooking, tech, or anything else, the frameworks and AI prompts adapt to your specific audience and content style." />
              <FAQ question="When do I get access?" answer="Immediately after purchase. You'll receive an email with download links within 60 seconds." />
              <FAQ question="What if it's not for me?" answer="No worries — you're covered by our 30-day money-back guarantee. If you don't find value in the toolkit, just email us and we'll refund you in full." />
            </div>
          </div>
        </Reveal>

        {/* Footer CTA */}
        <Reveal delay={700}>
          <div className="text-center pb-12">
            <button onClick={handlePurchase} disabled={purchasing} className="font-bold text-lg px-10 py-4 rounded-full border-none text-white cursor-pointer transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] min-h-[56px]" style={{ backgroundColor: "#7C3AED", boxShadow: "0 4px 14px rgba(124,58,237,0.3)" }}>
              {purchasing ? "Processing..." : `Get Instant Access → £${orderBump ? "29" : "17"}`}
            </button>
            {countdown > 0 && (
              <p className="text-xs mt-3" style={{ color: "#9CA3AF" }}>This offer expires in {minutes}:{seconds.toString().padStart(2, "0")}</p>
            )}
          </div>
        </Reveal>

        {/* Footer */}
        <footer className="py-8" style={{ borderTop: "1px solid #E5E7EB" }}>
          <div className="flex items-center justify-center gap-4 text-xs" style={{ color: "#9CA3AF" }}>
            <Link to="/privacy-policy" className="hover:text-gray-600 transition-colors">Privacy Policy</Link>
            <Link to="/terms-of-service" className="hover:text-gray-600 transition-colors">Terms</Link>
          </div>
        </footer>
      </div>
    </div>
  );
}
